import type { LucideIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

/**
 * A single headline number.
 *
 * Deliberately not a chart: one value over no dimension has no shape to plot,
 * and a sparkline with no axis would imply a trend the data does not carry.
 * The number is the visualisation.
 *
 * Degrades per-card — the dashboard's panels come from separate endpoints with
 * separate permissions, and one 403 should grey out one tile rather than blank
 * the page.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  isLoading,
  isError,
  className,
}: {
  label: string;
  value: number | undefined;
  icon: LucideIcon;
  isLoading?: boolean;
  isError?: boolean;
  className?: string;
}) {
  return (
    <Card className={cn('gap-0 py-4', className)}>
      <CardContent className="px-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {label}
          </p>
          <Icon className="text-muted-foreground/60 size-3.5 shrink-0" aria-hidden />
        </div>

        {isLoading ? (
          <Skeleton className="mt-2 h-7 w-20" />
        ) : isError ? (
          <p className="text-muted-foreground mt-2 text-sm">Unavailable</p>
        ) : (
          <p className="font-display mt-1.5 text-2xl font-bold tabular-nums">
            {formatNumber(value)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
